import React, {useCallback, useEffect, useId, useMemo, useRef, useState} from 'react';

import {ListController, ListSelectMode, ListValue} from '../list';
import {cls} from '../../utils';
import {useOnClickOutside} from '../../use-on-click-outside';
import {DropDownContext} from './drop-down-context';

import css from './drop-down.module.css';

export type DropDownProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'onSelect'> & {
	children?: React.ReactNode;
	/** режим выделения, как у списка; без него выпадашка ничего не выделяет */
	selectMode?: ListSelectMode;
	/** выделенные значения (управляемый режим) */
	value?: ListValue[];
	/** выделенные значения на старте (неуправляемый режим) */
	defaultValue?: ListValue[];
	onChange?(values: ListValue[], event: React.SyntheticEvent): void;
	/** текст в поле ввода триггера (управляемый режим) */
	inputValue?: string;
	/** текст в поле ввода триггера на старте; включает текстовое состояние */
	defaultInputValue?: string;
	onInputChange?(value: string, event: React.SyntheticEvent): void;
	/** открыта ли выпадашка (управляемый режим) */
	open?: boolean;
	defaultOpen?: boolean;
	onOpenChange?(open: boolean, event?: React.SyntheticEvent): void;
	/** закрывать после выбора; по умолчанию закрывается везде, кроме множественного выбора */
	closeOnSelect?: boolean;
	/**
	 * Виртуальный фокус: курсор остаётся в триггере, стрелки подсвечивают пункты списка.
	 * По умолчанию включён, когда в триггере поле ввода.
	 */
	virtualFocus?: boolean;
	disabled?: boolean;
};

export const DropDown: React.FC<DropDownProps> = ({
	children,
	selectMode,
	value,
	defaultValue,
	onChange,
	inputValue,
	defaultInputValue,
	onInputChange,
	open,
	defaultOpen,
	onOpenChange,
	closeOnSelect,
	virtualFocus,
	disabled,
	className,
	onKeyDown,
	...props
}) => {
	const rootRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const dropdownId = useId();
	const listController = useRef<ListController>(null);
	const [activeDescendant, setActiveDescendant] = useState<string | undefined>();

	const [openState, setOpenState] = useState(!!defaultOpen);
	const [selectedState, setSelectedState] = useState<ListValue[]>(defaultValue || []);
	const [inputState, setInputState] = useState(defaultInputValue || '');

	const isOpen = open !== undefined ? open : openState;
	const selected = value !== undefined ? value : selectedState;
	const withInput = inputValue !== undefined || defaultInputValue !== undefined || !!onInputChange;
	const text = inputValue !== undefined ? inputValue : inputState;

	const setOpen = useCallback(
		(next: boolean, event?: React.SyntheticEvent) => {
			if (open === undefined) setOpenState(next);
			onOpenChange?.(next, event);
		},
		[open, onOpenChange],
	);

	const close = useCallback(() => setOpen(false), [setOpen]);
	useOnClickOutside(isOpen && [rootRef], close);

	const toggle = useCallback((event: React.SyntheticEvent) => setOpen(!isOpen, event), [isOpen, setOpen]);

	const select = useCallback(
		(values: ListValue[], event: React.SyntheticEvent) => {
			if (value === undefined) setSelectedState(values);
			onChange?.(values, event);
			const shouldClose = closeOnSelect !== undefined ? closeOnSelect : selectMode !== 'multiple';
			if (shouldClose) {
				setOpen(false, event);
				triggerRef.current?.focus();
			}
		},
		[value, onChange, closeOnSelect, selectMode, setOpen],
	);

	const setInputValue = useCallback(
		(next: string, event: React.SyntheticEvent) => {
			if (inputValue === undefined) setInputState(next);
			onInputChange?.(next, event);
			if (!isOpen) setOpen(true, event);
		},
		[inputValue, onInputChange, isOpen, setOpen],
	);

	const focusFirstItem = useCallback(() => {
		const root = dropdownRef.current;
		// сам список тоже фокусируемый, поэтому сначала ищем пункт внутри него
		const item =
			root?.querySelector<HTMLElement>('[data-list-item] a[href], [data-list-item] button') ||
			root?.querySelector<HTMLElement>('a[href], button, [tabindex]:not([tabindex="-1"])');
		item?.focus();
	}, []);

	// слушаем нативно: событие из выпадашки не всегда доходит до React-обработчика корня
	useEffect(() => {
		const root = rootRef.current;
		if (!root || !isOpen) return;
		const handleEscape = (event: KeyboardEvent) => {
			if (event.key !== 'Escape' || event.defaultPrevented) return;
			setOpen(false);
			triggerRef.current?.focus();
			event.preventDefault();
			event.stopPropagation();
		};
		root.addEventListener('keydown', handleEscape);
		return () => root.removeEventListener('keydown', handleEscape);
	}, [isOpen, setOpen]);

	const context = useMemo(
		() => ({
			open: isOpen,
			setOpen,
			toggle,
			disabled,
			selectMode,
			selected,
			select,
			inputValue: withInput ? text : undefined,
			setInputValue,
			triggerRef,
			dropdownRef,
			dropdownId,
			focusFirstItem,
			virtualFocus: virtualFocus ?? withInput,
			listController,
			activeDescendant: isOpen ? activeDescendant : undefined,
			setActiveDescendant,
		}),
		[
			isOpen,
			setOpen,
			toggle,
			disabled,
			selectMode,
			selected,
			select,
			withInput,
			text,
			setInputValue,
			dropdownId,
			focusFirstItem,
			virtualFocus,
			activeDescendant,
		],
	);

	return (
		<DropDownContext.Provider value={context}>
			<div {...props} ref={rootRef} className={cls(css.root, className)} onKeyDown={onKeyDown}>
				{children}
			</div>
		</DropDownContext.Provider>
	);
};
